import { datetimeSchema } from './datetime'
import { isNull } from 'lodash'
import { z } from 'zod'
import { ALERT_STATUS, RELEVANCE_OPTIONS } from '../consts'
import { officeActionEventSchema } from './office-actions'
import { catchWithSentryCapture } from '../utils'

export const ALERT_TYPES = ['similar_patent', 'office_action_event'] as const

const baseUserSchema = z
  .object({
    id: z.string().min(1),
    full_name: z.string(),
  })
  .transform((user) => ({
    id: user.id,
    fullName: user.full_name,
  }))

const baseMonitorSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
})

export const patentNaturalRelevanceSchema = z.enum(RELEVANCE_OPTIONS)
export const noRelevanceSchema = z
  .enum(RELEVANCE_OPTIONS)
  .nullable()
  .transform((value) => value ?? 'none')

export const apiAlertSchema = z.object({
  id: z.string().min(1),
  monitored_document: z
    .object({ id: z.string(), document_number: z.string(), title: z.string() })
    .nullable()
    .optional(),
  alert_document_number: z.string(),
  status: z.enum(ALERT_STATUS),
  assignee: baseUserSchema.nullable().optional(),
  created_at: datetimeSchema,
  updated_at: datetimeSchema,
  relevance_analysis: z.string().nullable().optional().default(null),
  monitor: baseMonitorSchema,
  comment_count: z.number().nonnegative(),
  monitor_check_log_id: z.string(),
  detailed_relevance_analysis: z.string().nullable().optional().default(null),
  relevance: z
    .enum(RELEVANCE_OPTIONS)
    .nullable()
    .transform((value) => value ?? 'none'),
  viewed: z.boolean().optional().default(false),
  monitor_check_log_filters: z
    .object({
      id: z.string(),
      keyword_text_query: z.string().nullable(),
    })
    .nullable()
    .default(null),

  type: z
    .enum(ALERT_TYPES)
    // This isn't a promise chain.
    // eslint-disable-next-line unicorn/prefer-top-level-await
    .catch(catchWithSentryCapture('similar_patent' as const, 'apiAlertSchema')),
  office_action_event_code: officeActionEventSchema.optional().nullable(),
  office_action_event_date: datetimeSchema.optional().nullable(),
})

export type ApiAlert = z.infer<typeof apiAlertSchema>

const viewedStatusSchema = z.enum(['unread', 'open', 'archived'])
export type ViewedStatus = z.infer<typeof viewedStatusSchema>

export const alertSchema = apiAlertSchema.transform((alert) => {
  const {
    monitored_document,
    alert_document_number: alert_document_number,
    created_at,
    updated_at,
    relevance_analysis,
    comment_count,
    monitor_check_log_id,
    detailed_relevance_analysis,
    monitor_check_log_filters,
    office_action_event_code,
    office_action_event_date,
    ...alertFields
  } = alert
  let viewedStatus = viewedStatusSchema.parse('unread')
  if (alert.viewed) {
    viewedStatus = viewedStatusSchema.parse('open')
  }
  if (alert.status === 'archived') {
    viewedStatus = viewedStatusSchema.parse('archived')
  }

  return {
    ...alertFields,
    monitoredDocument: monitored_document,
    alertDocumentNumber: alert_document_number,
    createdAt: created_at,
    updatedAt: updated_at,
    relevanceAnalysis: relevance_analysis,
    commentCount: comment_count,
    monitorCheckLogId: monitor_check_log_id,
    detailedRelevanceAnalysis: detailed_relevance_analysis,
    viewedStatus,
    monitorCheckLogFilters: isNull(monitor_check_log_filters)
      ? null
      : {
          id: monitor_check_log_filters.id,
          keywordTextQuery: monitor_check_log_filters.keyword_text_query,
        },
    officeActionEventCode: office_action_event_code,
    officeActionEventDate: office_action_event_date,
  }
})

export type Alert = z.infer<typeof alertSchema>

export const apiAlertWithTitleSchema = apiAlertSchema.extend({
  alert_document_title: z.string(),
  alert_document_current_assignees: z
    .array(z.string())
    .optional()
    .default([])
    .transform((currentAssignees) =>
      currentAssignees.length > 0 ? currentAssignees.join(', ') : '-'
    ),
})

export const alertWithTitleSchema = apiAlertWithTitleSchema.transform(
  (alert) => {
    const {
      monitored_document,
      alert_document_number,
      alert_document_title,
      alert_document_current_assignees,
      created_at,
      updated_at,
      relevance_analysis,
      comment_count,
      monitor_check_log_id,
      office_action_event_code,
      office_action_event_date,
      ...alertFields
    } = alert

    let viewedStatus = viewedStatusSchema.parse('unread')
    if (alert.viewed) {
      viewedStatus = viewedStatusSchema.parse('open')
    }
    if (alert.status === 'archived') {
      viewedStatus = viewedStatusSchema.parse('archived')
    }

    return {
      ...alertFields,
      monitoredDocument: monitored_document,
      alertDocumentNumber: alert_document_number,
      alertDocumentTitle: alert_document_title,
      alertDocumentCurrentAssignees: alert_document_current_assignees,
      createdAt: created_at,
      updatedAt: updated_at,
      relevanceAnalysis: relevance_analysis,
      commentCount: comment_count,
      monitorCheckLogId: monitor_check_log_id,
      viewedStatus,
      officeActionEventCode: office_action_event_code,
      officeActionEventDate: office_action_event_date,
    }
  }
)

export type PaginatedAlert = z.infer<typeof alertWithTitleSchema>
